import { CommonModule } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, PRIMARY_OUTLET, Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { BehaviorSubject, filter, Subject, takeUntil } from 'rxjs';

// Einfaches Interface für Breadcrumb-Einträge
export interface Breadcrumb {
  label: string;
  url?: string;
}

@Component({
  selector: '[app-breadcrumb]',
  standalone: true,
  imports: [CommonModule, RouterModule, ButtonModule],
  providers: [Location],
  templateUrl: './breadcrumb.component.html',
  // Kein eigenes CSS-File notwendig – Tailwind CSS Klassen werden im Template benutzt
})
export class BreadcrumbComponent implements OnDestroy {
  // Observable für die Breadcrumb-Liste, damit AsyncPipe im Template verwendet werden kann
  private _breadcrumbs$ = new BehaviorSubject<Breadcrumb[]>([]);
  breadcrumbs$ = this._breadcrumbs$.asObservable();

  // Subscription-Teardown Subject für sauberes Unsubscribe
  private _destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
  ) {
    // NavigationEnd-Events abonnieren, um Breadcrumbs bei jeder Navigation neu zu berechnen
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this._destroy$)
    ).subscribe(() => {
      const root = this.route.root;
      const breadcrumbs: Breadcrumb[] = this.buildBreadcrumbs(root);
      this._breadcrumbs$.next(breadcrumbs);
    });

    // Initiale Breadcrumb-Berechnung (z.B. bei Direktaufruf einer tiefen URL)
    const initialCrumbs = this.buildBreadcrumbs(this.route.root);
    this._breadcrumbs$.next(initialCrumbs);
  }

  ngOnDestroy(): void {
    // Subscription beenden, um Memory-Leaks zu vermeiden
    this._destroy$.next();
    this._destroy$.complete();
  }

  /**
   * Rekursive Hilfsfunktion, die aus der ActivatedRouteSnapshot-Hierarchie die Breadcrumb-Liste erstellt.
   * @param route Aktuelle Route, von der aus die Breadcrumbs generiert werden (beginnend bei route.root).
   * @param url   Aktuell aufgelaufener URL-Pfad (für rekursive Aufrufe).
   * @param breadcrumbs Aktuell aufgebaute Breadcrumb-Liste (für rekursive Aufrufe).
   */
  private buildBreadcrumbs(route: ActivatedRoute, url: string = '', breadcrumbs: Breadcrumb[] = []): Breadcrumb[] {
    const children: ActivatedRoute[] = route.children;
    if (children.length === 0) {
      return breadcrumbs; // keine weiteren Teilrouten
    }

    // Nur den Primary-Outlets folgen (keine Nebenrouten berücksichtigen)
    const child = children.find(c => c.outlet === PRIMARY_OUTLET);
    if (!child || !child.snapshot) {
      return breadcrumbs;
    }

    // Wenn der Routenpfad leer ist, diesen Schritt überspringen (z.B. Placeholder für Lazy Module oder Container-Routen)
    if (child.snapshot.url.length === 0) {
      return this.buildBreadcrumbs(child, url, breadcrumbs);
    }

    // Aktuellen URL-Teil hinzufügen
    const routeURL: string = child.snapshot.url.map(segment => segment.path).join('/');
    if (routeURL) {
      url += `/${routeURL}`;
    }

    // Breadcrumb-Label aus den Routen-Informationen bestimmen
    let labelData = child.snapshot.data['breadcrumb'];
    // Explizit null bedeutet: Breadcrumb für diese Route überspringen
   /* if (labelData === null) {
      return this.buildBreadcrumbs(child, url, breadcrumbs);
    }*/

    // Wenn breadcrumb als Funktion definiert ist: ausführen mit den verfügbaren Route-Data
    if (typeof labelData === 'function') {
      labelData = labelData(child.snapshot.data);
    }

   
    
    // Wenn breadcrumb ein Array von Breadcrumb-Objekten ist: alle hinzufügen
    if (Array.isArray(labelData)) {
      for (const bc of labelData) {
        if (bc && bc.label) {
          let label: string | undefined = bc.label;
          let urlTransform: string | undefined = bc.url;
          if(typeof bc.label === 'function') {
            label = bc.label(child.snapshot.data);
          }
          if (typeof bc.url === 'function') {
            urlTransform = bc.url(child.snapshot.data);
          }
          breadcrumbs.push({
            label: label ?? bc.label,
            url: urlTransform ?? url  // falls kein URL angegeben, aktuellen URL-Pfad nutzen
          });
        } else {
          let label: string | undefined = bc.label;
          if (bc.label === undefined) {
            // Falls kein Label definiert ist, prüfen ob ein dynamischer Name im Navigation State mitgegeben wurde
            const state = window.history.state as { [key: string]: any };
            if (state && state['breadcrumbName']) {
              label = state['breadcrumbName'];
              if(typeof label === 'string' && label?.includes("/")) {
                var parts = label.split("/");
                parts.forEach((part, index) => {
                  if(index === (parts.length - 1)) return; // Letztes Segment überspringen
                  breadcrumbs.push({
                    label: part,
                    url: bc.url ?? url  // falls kein URL angegeben, aktuellen URL-Pfad nutzen
                  }); // Jedes Segment dekodieren
                });

                label = parts[parts.length - 1]; // Nur den letzten Teil des Pfads verwenden
              }
              
             
              
            } else if (routeURL) {
              // Fallback: Route-Teil als Label (z.B. Param-Wert oder Pfadsegment)
              label = decodeURIComponent(routeURL);
            } else {
              label = undefined;
            }

            if (label) {
              breadcrumbs.push({
                label: label,
                url: bc.url ?? url  // falls kein URL angegeben, aktuellen URL-Pfad nutzen
              });
            }
        }
      }
    }
    } else {
      // Kein Array: labelData ist nun entweder String (statisch oder aus Function), oder undefined
      let label: string | undefined = labelData;
      if (label === undefined) {
        // Falls kein Label definiert ist, prüfen ob ein dynamischer Name im Navigation State mitgegeben wurde
        const state = window.history.state as { [key: string]: any };
        if (state && state['breadcrumbName']) {
          label = state['breadcrumbName'];
          if(typeof label === 'string' && label?.includes("/")) {
            var parts = label.split("/");
            parts.forEach((part, index) => {
              if(index === (parts.length - 1)) return; // Letztes Segment überspringen
              breadcrumbs.push({
                label: part,
                url: url  // falls kein URL angegeben, aktuellen URL-Pfad nutzen
              }); // Jedes Segment dekodieren
            });

            label = parts[parts.length - 1]; // Nur den letzten Teil des Pfads verwenden
          }
        } else if (routeURL) {
          // Fallback: Route-Teil als Label (z.B. Param-Wert oder Pfadsegment)
          label = decodeURIComponent(routeURL);
        } else {
          label = undefined;
        }
      }

      if (label) {
        breadcrumbs.push({ label, url });
      }
    }

    // Rekursiv tiefer in die nächste aktivierte Route gehen
    return this.buildBreadcrumbs(child, url, breadcrumbs);
  }
}

